import React, { useState } from 'react';
import { API_CONFIG } from '../../config/api';
import { getHeaders } from '../../utils/api';

interface ImportNewsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

interface ImportNewsFormData {
  link: string;
  keywords: string;
  pubDate: string;
  pubDateTZ: string;
  description: string;
  imageUrl: string;
  language: string;
  sourceName: string;
  sourceIcon: string;
  creator: string;
}

interface ImportNewsRequest {
  link: string;
  keywords?: (string | null)[] | null;
  pubDate?: string | null;
  pubDateTZ?: string | null;
  description?: string | null;
  imageUrl?: string | null;
  language?: string | null;
  sourceName?: string | null;
  sourceIcon?: string | null;
  creator?: string | null;
}

const ImportNewsModal: React.FC<ImportNewsModalProps> = ({ isOpen, onClose, onSave }) => {
  // Get current date and time in the format required for datetime-local input
  const getCurrentDateTime = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState<ImportNewsFormData>({
    link: '',
    keywords: '',
    pubDate: getCurrentDateTime(),
    pubDateTZ: 'Europe/Berlin',
    description: '',
    imageUrl: '',
    language: 'en',
    sourceName: '',
    sourceIcon: '',
    creator: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isScraping, setIsScraping] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleScrapNews = async () => {
    if (!formData.link.trim()) {
      setError('Please enter an article link first');
      return;
    }

    setIsScraping(true);
    setError(null);

    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/admin/news/import/by-scrapper?link=${encodeURIComponent(formData.link)}`, {
        method: 'GET',
        headers: getHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to scrap news data');
      }

      const scrapedData: ImportNewsFormData = await response.json();

      // Populate form with scraped data, but don't import date
      setFormData(prev => ({
        ...prev,
        ...scrapedData,
        pubDate: prev.pubDate // Keep the current date/time, don't import from scraper
      }));

    } catch (err) {
      setError('Failed to scrap news data. Please try again.');
      console.error('Error:', err);
    } finally {
      setIsScraping(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Format the date to the required format "2025-06-19 19:04:39"
      const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      };

      // Convert comma-separated strings to arrays and format date
      const requestData: ImportNewsRequest = {
        link: formData.link,
        keywords: formData.creator.split(',').map(c => c.trim()).filter(Boolean),
        pubDate: formatDate(formData.pubDate),
        pubDateTZ: formData.pubDateTZ || null,
        description: formData.description || null,
        imageUrl: formData.imageUrl || null,
        language: formData.language || null,
        sourceName: formData.sourceName || null,
        sourceIcon: formData.sourceIcon || null,
        creator: formData.creator || null
      };

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.NEWS.IMPORT_BY_LINK}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        throw new Error('Failed to import news');
      }

      onSave();
      onClose();
      // Reset form
      setFormData({
        link: '',
        keywords: '',
        pubDate: getCurrentDateTime(),
        pubDateTZ: 'Europe/Berlin',
        description: '',
        imageUrl: '',
        language: 'en',
        sourceName: '',
        sourceIcon: '',
        creator: ''
      });
    } catch (err) {
      setError('Failed to import news. Please try again.');
      console.error('Error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px' }}>
        <div className="modal-header">
          <h3>Import News by Link</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <label htmlFor="link">Article Link *</label>
              <button
                type="button"
                onClick={handleScrapNews}
                disabled={isScraping || !formData.link.trim()}
                style={{
                  background: '#17a2b8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                {isScraping ? 'Scraping...' : 'Scrap News'}
              </button>
            </div>
            <input
              type="url"
              id="link"
              name="link"
              value={formData.link}
              onChange={handleInputChange}
              placeholder="https://example.com/news/article"
              required
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 2 }}>
              <label htmlFor="pubDate">Publication Date *</label>
              <input
                type="datetime-local"
                id="pubDate"
                name="pubDate"
                value={formData.pubDate}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="pubDateTZ">Publication Timezone</label>
              <select
                id="pubDateTZ"
                name="pubDateTZ"
                value={formData.pubDateTZ}
                onChange={handleInputChange}
              >
                <option value="Europe/Berlin">Europe/Berlin</option>
                <option value="UTC">UTC</option>
                <option value="America/New_York">America/New_York</option>
                <option value="Asia/Tokyo">Asia/Tokyo</option>
                <option value="Australia/Sydney">Australia/Sydney</option>
              </select>
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="language">Language *</label>
              <select
                id="language"
                name="language"
                value={formData.language}
                onChange={handleInputChange}
                required
              >
                <option value="en">English</option>
                <option value="de">German</option>
                <option value="es">Spanish</option>
                <option value="fr">French</option>
                <option value="it">Italian</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Comprehensive analysis of the current economic situation and market trends."
              rows={3}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="imageUrl">Image URL *</label>
            <input
              type="url"
              id="imageUrl"
              name="imageUrl"
              value={formData.imageUrl}
              onChange={handleInputChange}
              placeholder="https://example.com/images/article.jpg"
              required
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="sourceName">Source Name *</label>
              <input
                type="text"
                id="sourceName"
                name="sourceName"
                value={formData.sourceName}
                onChange={handleInputChange}
                placeholder="Business Daily"
                required
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="creator">Creators</label>
              <input
                type="text"
                id="creator"
                name="creator"
                value={formData.creator}
                onChange={handleInputChange}
                placeholder="Jane Smith, Mike Johnson"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="sourceIcon">Source Icon URL</label>
            <input
              type="url"
              id="sourceIcon"
              name="sourceIcon"
              value={formData.sourceIcon}
              onChange={handleInputChange}
              placeholder="https://example.com/icons/businessdaily.png"
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <small style={{ color: '#6c757d', fontSize: '0.875rem', display: 'block' }}>
              Separate creators with commas
            </small>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-footer">
            <button type="button" className="cancel-button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="submit-button" disabled={isSubmitting}>
              {isSubmitting ? 'Importing...' : 'Import News'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ImportNewsModal;